
import { defineComponent, computed, ref, watch, onBeforeMount } from "vue";
import { useStore } from "vuex";
import TUIViewer from "../../components/application/TUIViewer.vue";
import TimeFormatter from "./TimeFormatter.vue";
import QuestionFormNavigation from "../../components/application/buttons/QuestionFormNavigation.vue";
import QuestionFormStar from "./QuestionFormStar.vue";
import QuestionFormSingleAnswer from "./QuestionFormSingleAnswer.vue";
import QuestionFormMoreAnswer from "./QuestionFormMoreAnswer.vue";
import QuestionFormText from "./QuestionFormText.vue";
import { Content } from "./helpers";
import { formattedDuration } from "../../components/mixins";
import { formatTime } from "../exercise/exercise_helpers";
import { SearchForm } from "../../../../domain/entities/content/search_form";
import FinishQuestionForm from "./FinishQuestionForm.vue";
import { useRoute, useRouter } from "vue-router";
import { updateQuery, removeQuery } from "../../utils/routing_utils";

export default defineComponent({
  name: "SearchFormCover",
  components: {
    FinishQuestionForm,
    QuestionFormNavigation,
    QuestionFormText,
    QuestionFormMoreAnswer,
    QuestionFormSingleAnswer,
    QuestionFormStar,
    TUIViewer,
    TimeFormatter
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const currentPage = ref(0);
    const totalPages = ref(0);
    const questionComponent = ref();
    const trainingID = Number(route.params.course_id);
    const showStartButton = computed(
      () => currentPage.value === 0 && !currentFormType.value
    );

    const currentContent = computed<Content>(
      () => store.getters["content_module/currentContent"]
    );

    const searchForm = computed<SearchForm>(
      () => store.getters["content_module/exercise/exercise"]
    );

    const currentQuestion = computed(
      () => store.getters["question_form_module/evaluationQuestion"]
    );

    const isFinished = computed(
      () => currentContent.value.searchForm.statusForm.finished
    );

    const formButton = computed(() => {
      if (currentContent.value.status.progress > 0) {
        return "Continuar respondendo o formulário";
      } else {
        return "Responder formulário";
      }
    });

    const isAnswered = computed(() => {
      const response = currentQuestion.value?.response;
      if (!response) return false;
      return (
        !!response.answerText ||
        !!response.answerId ||
        (response.multiAnswerIds?.length ?? 0) > 0 ||
        !!response.stars
      );
    });

    const currentFormType = ref<number | null>(null);

    const onClickStart = async () => {
      try {
        if (totalPages.value === 0) {
          totalPages.value =
            currentContent.value.searchForm?.numberOfQuestionsForm ?? 0;
        }
        if (currentPage.value < totalPages.value) {
          currentPage.value++;
        }
        await loadPage(currentPage.value);
      } catch (e) {
        console.error("Erro ao iniciar formulário", e);
      }
    };

    const loadPage = async (page: number) => {
      const searchFormID = currentContent.value.searchForm?.id;
      if (!searchFormID) return;

      await store.dispatch("question_form_module/get", {
        searchFormID,
        trainingID,
        page
      });

      const evalRes = store.getters["question_form_module/evaluationQuestion"];

      if (evalRes.response) {
        const response = evalRes.response;
        evalRes.question.answerText = response.answerText ?? "";
        evalRes.question.answerId = response.answerId ?? NaN;
        evalRes.question.multiAnswerIds = response.multiAnswerIds ?? [];
        evalRes.question.stars = response.stars ?? 0;
      }

      currentFormType.value = evalRes.question.type;
    };

    const onClickNext = async () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
        await loadPage(currentPage.value);
      }
    };

    onBeforeMount(async () => {
      const searchFormID = currentContent.value.searchForm?.id;
      if (!searchFormID) return;

      const pageFromQuery = Number(route.query.page || 0);
      const maxPages =
        currentContent.value.searchForm?.numberOfQuestionsForm ?? 0;

      if (pageFromQuery > 0 && pageFromQuery <= maxPages) {
        currentPage.value = pageFromQuery;
        totalPages.value = maxPages;
        await loadPage(currentPage.value);
      }
    });

    watch(currentPage, async newPage => {
      if (newPage > 0) {
        await updateQuery(router, "page", String(newPage));
      } else {
        await removeQuery(router, "page");
      }
    });

    watch(
      () => route.params.content_id,
      async (newID, oldID) => {
        if (newID && newID !== oldID) {
          await removeQuery(router, "page");
          currentPage.value = 0;
          currentFormType.value = null;
        }
      }
    );

    const onClickPrevious = async () => {
      if (currentPage.value > 1) {
        currentPage.value--;
        await loadPage(currentPage.value);
      } else if (currentPage.value === 1) {
        currentPage.value = 0;
      }
    };

    const componentMap: Record<number, any> = {
      1: QuestionFormStar,
      2: QuestionFormSingleAnswer,
      3: QuestionFormMoreAnswer,
      4: QuestionFormText
    };

    const activeComponent = computed(() =>
      currentFormType.value ? componentMap[currentFormType.value] : null
    );

    return {
      currentContent,
      currentPage,
      totalPages,
      searchForm,
      currentFormType,
      activeComponent,
      currentQuestion,
      questionComponent,
      isFinished,
      isAnswered,
      showStartButton,
      formButton,
      onClickStart,
      onClickNext,
      onClickPrevious,
      formattedDuration,
      formatTime
    };
  }
});
